import { createSlice, createSelector } from '@reduxjs/toolkit';

// The download / share selection ticked on the browse cards and on View All.
//
// It lives in the store, not in a component, so it survives the two things
// that used to wipe it: the swap between the browse list and View All (the
// page renders one OR the other, so changing the keyword unmounted the
// component holding the ticks) and a new search replacing the rows on
// screen. It is deliberately NOT persisted across reloads (see store.js) —
// rows carry approval state that goes stale.
//
// One entry per ticked row: a plain row is keyed by its video id, a segment
// row (search-by-segment results) by its own segment id, in separate key
// spaces ("v:"/"s:") — segment ids and video ids are different sequences and
// can collide numerically. Several rows can belong to one video. The pages
// read the derived shapes below.
const keyOf = ({ videoId, segmentId }) => (segmentId != null ? `s:${segmentId}` : `v:${videoId}`);

export const selectionSlice = createSlice({
    name: 'selection',
    initialState: { rows: {} },
    reducers: {
        // { videoId, segmentId?, typeFlag, row, checked }
        toggleRow(state, { payload }) {
            const k = keyOf(payload);
            if (payload.checked) state.rows[k] = { id: payload.segmentId != null ? payload.segmentId : payload.videoId, videoId: payload.videoId, typeFlag: payload.typeFlag, row: payload.row };
            else delete state.rows[k];
        },
        // every row of one video (the bar's ×, and unticking a browse card)
        removeVideo(state, { payload: videoId }) {
            for (const k of Object.keys(state.rows)) {
                if (String(state.rows[k].videoId) === String(videoId)) delete state.rows[k];
            }
        },
        clearSelection(state) {
            state.rows = {};
        },
    },
});

export const { toggleRow, removeVideo, clearSelection } = selectionSlice.actions;
export default selectionSlice.reducer;

const rows = (state) => state.selection.rows;
const entries = createSelector([rows], (r) => Object.values(r));
// [{ video_id, type_flag, id }] — one per ticked row (the checkbox lookup)
export const selectSelectedItems = createSelector([entries], (es) => es.map((e) => ({ video_id: e.videoId, type_flag: e.typeFlag, id: e.id })));
// unique video ids (share flows)
export const selectSelectedVideoIds = createSelector([entries], (es) => {
    const seen = new Set(); const out = [];
    for (const e of es) { const k = String(e.videoId); if (!seen.has(k)) { seen.add(k); out.push(e.videoId); } }
    return out;
});
// one row object per video (the download set the request flow triages)
export const selectSelectedVideos = createSelector([entries], (es) => {
    const seen = new Set(); const out = [];
    for (const e of es) { const k = String(e.videoId); if (!seen.has(k)) { seen.add(k); out.push(e.row); } }
    return out;
});
